/**
 * Horizontal product media gallery — Swiper + slider-controls markup (navigation, pagination, fraction).
 */

import { Component } from "./component-base.js";
import { ThemeBus } from "./events-bus.js";
import { EVENTS } from "./event-constants.js";
import { SWIPER_DRAG_DEFAULTS } from "./swiper-feel.js";

export class HorizontalProductMediaGalleryComponent extends Component {
  swiper = null;

  /** @type {HTMLElement | null} */
  fractionEl = null;

  /** @type {((e: Event) => void) | null} */
  #customPaginationClick = null;

  /** @type {number} */
  #swiperWaitAttempts = 0;
  static #MAX_SWIPER_WAIT = 80;

  connectedCallback() {
    super.connectedCallback();
    ThemeBus.addEventListener(EVENTS.variantChange, this.#onVariantChange);
    this.#waitForSwiperAndInit();
  }

  disconnectedCallback() {
    ThemeBus.removeEventListener(EVENTS.variantChange, this.#onVariantChange);
    this.#teardownCustomPaginationClick();
    this.#destroySwiper();
    super.disconnectedCallback();
  }

  #onVariantChange = (event) => {
    const variant = event.detail?.variant;
    if (!variant?.featured_media?.id || !this.swiper) return;

    const id = String(variant.featured_media.id);
    const slides = this.querySelectorAll(".swiper-slide[data-media-id]");
    const index = Array.from(slides).findIndex((slide) => slide.dataset.mediaId === id);
    if (index !== -1) {
      this.swiper.slideTo(index);
    }
  };

  #teardownCustomPaginationClick() {
    if (this.#customPaginationClick) {
      this.removeEventListener("click", this.#customPaginationClick);
      this.#customPaginationClick = null;
    }
  }

  #destroySwiper() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
    this.fractionEl = null;
  }

  #waitForSwiperAndInit() {
    if (typeof window.Swiper === "undefined") {
      this.#swiperWaitAttempts += 1;
      if (this.#swiperWaitAttempts < HorizontalProductMediaGalleryComponent.#MAX_SWIPER_WAIT) {
        setTimeout(() => this.#waitForSwiperAndInit(), 50);
      }
      return;
    }
    this.#initSwiper();
  }

  #initSwiper() {
    const el = this.querySelector(".swiper");
    if (!el) {
      return;
    }

    const slides = el.querySelectorAll(".swiper-slide");
    if (slides.length <= 1) {
      return;
    }

    this.#teardownCustomPaginationClick();
    this.#destroySwiper();

    const controlsRoot = this.querySelector("[data-slider-control]");
    const prev =
      this.querySelector(".horizontal-product-media-gallery__nav-prev") ||
      controlsRoot?.querySelector(".slider-navigation-prev");
    const next =
      this.querySelector(".horizontal-product-media-gallery__nav-next") ||
      controlsRoot?.querySelector(".slider-navigation-next");

    /** @type {Record<string, unknown>} */
    const options = {
      ...SWIPER_DRAG_DEFAULTS,
      slidesPerView: "auto",
      spaceBetween: 0,
      loop: false,
      watchOverflow: true,
      keyboard: { enabled: true },
    };

    if (prev || next) {
      /** @type {{ prevEl?: HTMLElement; nextEl?: HTMLElement }} */
      const nav = {};
      if (prev) nav.prevEl = prev;
      if (next) nav.nextEl = next;
      options.navigation = nav;
    }

    if (controlsRoot) {
      const paginationEl = controlsRoot.querySelector(".swiper-pagination");
      if (paginationEl) {
        const paginationType = paginationEl.dataset.paginationType || "bullets";
        const controlsPaginationColour = paginationEl.dataset.paginationColour || "#000000";

        if (paginationType === "progressbar_fraction") {
          this.fractionEl =
            paginationEl.parentElement?.querySelector("[data-slider-fraction]") ||
            controlsRoot.querySelector("[data-slider-fraction]");
          options.pagination = {
            el: paginationEl,
            type: "progressbar",
            clickable: false,
          };
        } else if (paginationType === "custom") {
          options.pagination = {
            el: paginationEl,
            type: "custom",
            clickable: true,
            renderCustom: (swiper, current, total) => {
              let bullets = "";
              for (let i = 1; i <= total; i += 1) {
                const isActive = i === current ? "opacity-100" : "opacity-50";
                bullets += `<span class="block w-10 h-1 rounded-full cursor-pointer transition-opacity duration-300 ${isActive}" style="background-color: ${controlsPaginationColour};" data-slide-index="${i}"></span>`;
              }
              return `<div class="flex gap-2 justify-center">${bullets}</div>`;
            },
          };
          this.#customPaginationClick = (e) => {
            const t = e.target;
            if (!t || !(t instanceof Element)) return;
            const bullet = t.closest("[data-slide-index]");
            if (bullet && this.swiper) {
              const index = parseInt(bullet.dataset.slideIndex || "0", 10) - 1;
              this.swiper.slideTo(index);
            }
          };
          this.addEventListener("click", this.#customPaginationClick);
        } else {
          options.pagination = {
            el: paginationEl,
            type: paginationType,
            clickable: true,
          };
        }
      }
    }

    try {
      this.swiper = new window.Swiper(el, options);
    } catch (err) {
      console.warn("[HorizontalProductMediaGallery] Swiper init failed:", err);
      return;
    }

    if (this.fractionEl && this.swiper) {
      const updateFraction = () => {
        const s = this.swiper;
        if (!s || !this.fractionEl) return;
        const current = s.activeIndex + 1;
        const total = s.slides.length;
        this.fractionEl.textContent = `${current} / ${total}`;
      };
      // One listener — slideChange + activeIndexChange both fire together and cause flicker.
      this.swiper.on("slideChange", updateFraction);
      updateFraction();
    }
  }
}

if (!customElements.get("horizontal-product-media-gallery-component")) {
  customElements.define("horizontal-product-media-gallery-component", HorizontalProductMediaGalleryComponent);
}

/**
 * Mobile-only strip for horizontal-gallery layout: one slide per view + floating pagination dots.
 */
class HorizontalProductMediaGalleryMobileComponent extends Component {
  swiper = null;

  /** @type {number} */
  #swiperWaitAttempts = 0;
  static #MAX_SWIPER_WAIT = 80;

  connectedCallback() {
    super.connectedCallback();
    ThemeBus.addEventListener(EVENTS.variantChange, this.#onVariantChangeMobile);
    this.#waitForSwiperMobile();
  }

  disconnectedCallback() {
    ThemeBus.removeEventListener(EVENTS.variantChange, this.#onVariantChangeMobile);
    this.#destroySwiperMobile();
    super.disconnectedCallback();
  }

  #onVariantChangeMobile = (event) => {
    const variant = event.detail?.variant;
    if (!variant?.featured_media?.id || !this.swiper) return;

    const id = String(variant.featured_media.id);
    const slides = this.querySelectorAll(".swiper-slide[data-media-id]");
    const index = Array.from(slides).findIndex((slide) => slide.dataset.mediaId === id);
    if (index !== -1) {
      this.swiper.slideTo(index);
    }
  };

  #destroySwiperMobile() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  #waitForSwiperMobile() {
    if (typeof window.Swiper === "undefined") {
      this.#swiperWaitAttempts += 1;
      if (this.#swiperWaitAttempts < HorizontalProductMediaGalleryMobileComponent.#MAX_SWIPER_WAIT) {
        setTimeout(() => this.#waitForSwiperMobile(), 50);
      }
      return;
    }
    this.#initSwiperMobile();
  }

  #initSwiperMobile() {
    const el = this.querySelector(".swiper");
    if (!el) {
      return;
    }

    const slides = el.querySelectorAll(".swiper-slide");
    if (slides.length <= 1) {
      return;
    }

    this.#destroySwiperMobile();

    const paginationEl = this.querySelector(".horizontal-product-media-gallery__mobile-pagination");

    /** @type {Record<string, unknown>} */
    const options = {
      ...SWIPER_DRAG_DEFAULTS,
      slidesPerView: 1,
      spaceBetween: 0,
      loop: false,
      watchOverflow: true,
    };

    if (paginationEl) {
      options.pagination = {
        el: paginationEl,
        clickable: true,
      };
    }

    try {
      this.swiper = new window.Swiper(el, options);
    } catch (err) {
      console.warn("[HorizontalProductMediaGalleryMobile] Swiper init failed:", err);
    }
  }
}

if (!customElements.get("horizontal-product-media-gallery-mobile-component")) {
  customElements.define("horizontal-product-media-gallery-mobile-component", HorizontalProductMediaGalleryMobileComponent);
}
